<template>
  <div class="trial-balance-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Page Header: Breadcrumb, Title, Date Filter & Actions -->
    <div class="page-header-wrap mb-4">
      <div class="d-flex flex-wrap align-center justify-space-between">
        <!-- Left: Breadcrumb + Title + Subtitle -->
        <div class="header-text-block">
          <PageBreadcrumb :items="[{ text: 'Accounts', to: '/Accounts' }, { text: 'Trial Balance' }]" class="mb-1" />
          <p class="page-subtitle mb-0">Summary of all closing debit and credit balances across ledger heads to ensure accounting equilibrium.</p>
        </div>

        <!-- Right: As of Date & Download PDF Action -->
        <div class="header-actions-group d-flex flex-wrap align-center mt-3 mt-sm-0">
          <!-- As of Date Picker -->
          <div class="date-picker-wrap mr-2">
            <v-menu
              ref="menu"
              v-model="menu"
              :close-on-content-click="false"
              :return-value.sync="asOf"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="asOf"
                  outlined
                  dense
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  class="date-input-field"
                  color="#9a6b27"
                  prepend-inner-icon="mdi-calendar-range"
                  prefix="As of:"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="asOf"
                no-title
                scrollable
                color="#9a6b27"
                @change="onDateChange(asOf)"
              ></v-date-picker>
            </v-menu>
          </div>

          <!-- Download PDF Button -->
          <v-btn
            depressed
            class="btn-download-pdf mr-2"
            @click="download()"
            :loading="appLoading"
          >
            <v-icon left size="16">mdi-file-pdf-box</v-icon>
            Download PDF
          </v-btn>

          <!-- Refresh Button -->
          <v-btn
            outlined
            depressed
            class="btn-action-outline btn-icon-only"
            @click="fetchTrialBalance()"
            title="Refresh Trial Balance"
          >
            <v-icon size="17" color="#64748b">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- 3 Summary Metrics Cards: Total Debit, Total Credit, Equilibrium Status -->
    <div class="summary-cards-grid mb-5">
      <!-- Card 1: Total Debit -->
      <div class="metric-card metric-card-debit">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="metric-label">TOTAL DEBITS</span>
          <div class="metric-icon-circle icon-debit">
            <v-icon size="16" color="#059669">mdi-arrow-bottom-left</v-icon>
          </div>
        </div>
        <div class="metric-value font-mono text-debit">
          ₹ {{ formatCurrency(totalDebit) }}
        </div>
        <div class="metric-caption">
          <span>Incoming & Asset Balances</span>
        </div>
      </div>

      <!-- Card 2: Total Credit -->
      <div class="metric-card metric-card-credit">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="metric-label">TOTAL CREDITS</span>
          <div class="metric-icon-circle icon-credit">
            <v-icon size="16" color="#2563eb">mdi-arrow-top-right</v-icon>
          </div>
        </div>
        <div class="metric-value font-mono text-credit">
          ₹ {{ formatCurrency(totalCredit) }}
        </div>
        <div class="metric-caption">
          <span>Liabilities, Capital & Income</span>
        </div>
      </div>

      <!-- Card 3: Equilibrium / Balance Status -->
      <div class="metric-card" :class="isBookBalanced ? 'metric-card-balanced' : 'metric-card-unbalanced'">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="metric-label">{{ isBookBalanced ? 'BOOK STATUS' : 'ATTENTION REQUIRED' }}</span>
          <div class="metric-icon-circle" :class="isBookBalanced ? 'icon-balanced' : 'icon-unbalanced'">
            <v-icon size="16" :color="isBookBalanced ? '#059669' : '#e11d48'">
              {{ isBookBalanced ? 'mdi-check-decagram' : 'mdi-alert-circle' }}
            </v-icon>
          </div>
        </div>
        <div class="metric-value" :class="isBookBalanced ? 'text-balanced' : 'text-unbalanced'">
          {{ isBookBalanced ? 'Books Balanced' : 'Out of Balance' }}
        </div>
        <div class="metric-caption">
          <span v-if="isBookBalanced">Debits equal Credits (Diff: ₹ 0.00)</span>
          <span v-else class="text-unbalanced-sub font-mono">Difference: ₹ {{ formatCurrency(differenceAmount) }}</span>
        </div>
      </div>
    </div>

    <!-- Out of Balance Notice Banner (Only if unbalanced) -->
    <v-alert
      v-if="!isBookBalanced && trialBalanceRows.length > 0"
      prominent
      border="left"
      color="#e11d48"
      colored-border
      elevation="0"
      class="unbalanced-alert-banner rounded-xl mb-5"
    >
      <div class="d-flex align-center justify-space-between flex-wrap">
        <div>
          <div class="alert-title font-weight-bold">Debit and Credit totals do not match</div>
          <div class="alert-subtitle">
            There is a discrepancy of <strong>₹ {{ formatCurrency(differenceAmount) }}</strong>. Please check that opening balances or journal entries are fully balanced.
          </div>
        </div>
        <v-btn
          outlined
          small
          class="btn-action-outline mt-2 mt-sm-0"
          @click="$router.push('/Accounts')"
        >
          <v-icon left size="14">mdi-book-edit-outline</v-icon>
          Review Vouchers
        </v-btn>
      </div>
    </v-alert>

    <!-- Main Table Card: Ledger Balances -->
    <v-card class="table-card rounded-xl" elevation="0">
      <!-- Card Top Bar with Title, Type Filter & Search -->
      <div class="table-card-top-bar d-flex flex-wrap align-center justify-space-between px-5 py-3 border-bottom">
        <div class="d-flex align-center my-1">
          <h2 class="section-title mb-0">Ledger Balances</h2>
          <span class="active-count-pill ml-2" v-if="trialBalanceRows">
            {{ filteredRows.length }} {{ filteredRows.length === 1 ? 'account' : 'accounts' }}
          </span>
        </div>

        <div class="d-flex flex-wrap align-center gap-2 my-1">
          <!-- Type Filter -->
          <v-select
            v-model="selectedTypeFilter"
            :items="typeFilterOptions"
            outlined
            dense
            hide-details
            class="filter-type-select mr-2"
            color="#9a6b27"
            prepend-inner-icon="mdi-filter-variant"
          ></v-select>

          <!-- Search Input -->
          <v-text-field
            v-model="searchQuery"
            outlined
            dense
            hide-details
            clearable
            placeholder="Search account name or code..."
            class="filter-search-input"
            color="#9a6b27"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>
      </div>

      <!-- Table Body -->
      <div class="table-responsive" v-if="filteredRows.length > 0">
        <table class="custom-table">
          <thead>
            <tr>
              <th class="col-sno text-center">#</th>
              <th class="col-name">ACCOUNT NAME</th>
              <th class="col-code">CODE</th>
              <th class="col-type">TYPE</th>
              <th class="col-debit text-right">DEBIT (₹)</th>
              <th class="col-credit text-right">CREDIT (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, i) in filteredRows"
              :key="i"
              class="table-row"
            >
              <!-- S.No -->
              <td class="col-sno text-center text-muted font-mono">
                {{ i + 1 }}
              </td>

              <!-- Account Name with Icon -->
              <td class="col-name">
                <div class="d-flex align-center">
                  <div class="account-icon-box mr-3" :class="getTypeIconBoxClass(item.type)">
                    <v-icon size="15" :color="getTypeColor(item.type)">{{ getTypeIcon(item.type) }}</v-icon>
                  </div>
                  <div>
                    <div class="account-name-text">{{ item.name }}</div>
                  </div>
                </div>
              </td>

              <!-- Account Code -->
              <td class="col-code">
                <span v-if="item.code && item.code !== '-'" class="code-badge font-mono">{{ item.code }}</span>
                <span v-else class="text-muted-dash">—</span>
              </td>

              <!-- Account Type Badge -->
              <td class="col-type">
                <span class="type-badge" :class="getTypeBadgeClass(item.type)">
                  <span class="type-dot" :style="{ backgroundColor: getTypeColor(item.type) }"></span>
                  {{ item.type }}
                </span>
              </td>

              <!-- Debit Amount -->
              <td class="col-debit text-right font-mono">
                <span v-if="item.debit && item.debit > 0" class="debit-val">
                  ₹ {{ formatCurrency(item.debit) }}
                </span>
                <span v-else class="text-muted-dash">—</span>
              </td>

              <!-- Credit Amount -->
              <td class="col-credit text-right font-mono">
                <span v-if="item.credit && item.credit > 0" class="credit-val">
                  ₹ {{ formatCurrency(item.credit) }}
                </span>
                <span v-else class="text-muted-dash">—</span>
              </td>
            </tr>
          </tbody>

          <!-- Table Footer Totals Row -->
          <tfoot class="table-totals-foot">
            <tr>
              <td colspan="4" class="totals-label-cell">
                <div class="d-flex align-center">
                  <v-icon size="16" :color="isBookBalanced ? '#059669' : '#e11d48'" class="mr-2">
                    {{ isBookBalanced ? 'mdi-scale-balance' : 'mdi-alert-circle' }}
                  </v-icon>
                  <span class="totals-text">TOTALS {{ isBookBalanced ? '(IN BALANCE)' : '(OUT OF BALANCE)' }}</span>
                </div>
              </td>
              <td class="col-debit text-right font-mono totals-debit-val">
                ₹ {{ formatCurrency(totalDebit) }}
              </td>
              <td class="col-credit text-right font-mono totals-credit-val">
                ₹ {{ formatCurrency(totalCredit) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center py-10 px-4">
        <div class="empty-icon-circle mx-auto mb-3">
          <v-icon size="36" color="#94a3b8">mdi-scale-balance</v-icon>
        </div>
        <h3 class="empty-title mb-1">
          {{ searchQuery || selectedTypeFilter !== 'All' ? 'No matching ledger accounts' : 'No account balances found' }}
        </h3>
        <p class="empty-desc mb-4">
          {{ searchQuery || selectedTypeFilter !== 'All'
            ? 'Try clearing your search query or type filter.'
            : 'No active account head transactions found as of ' + asOf + '.'
          }}
        </p>
        <v-btn
          v-if="searchQuery || selectedTypeFilter !== 'All'"
          outlined
          small
          class="btn-action-outline"
          @click="resetFilters"
        >
          <v-icon left size="14">mdi-filter-remove-outline</v-icon>
          Reset Filters
        </v-btn>
      </div>

      <!-- Table Footer / Legend -->
      <div class="table-card-footer d-flex flex-wrap align-center justify-space-between px-5 py-3 border-top" v-if="filteredRows.length > 0">
        <div class="footer-summary-text">
          Showing <strong>{{ filteredRows.length }}</strong> of <strong>{{ trialBalanceRows.length }}</strong> active ledger accounts as of <strong>{{ asOf }}</strong>
        </div>
        <div class="footer-legend d-none d-md-flex align-center gap-3">
          <span class="legend-item"><span class="type-dot bg-asset"></span> Asset</span>
          <span class="legend-item"><span class="type-dot bg-liability"></span> Liability</span>
          <span class="legend-item"><span class="type-dot bg-capital"></span> Capital</span>
          <span class="legend-item"><span class="type-dot bg-income"></span> Income</span>
          <span class="legend-item"><span class="type-dot bg-expense"></span> Expense</span>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      menu: false,
      asOf: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      trialBalanceRows: [],
      totalDebit: 0,
      totalCredit: 0,
      isBalanced: true,
      searchQuery: "",
      selectedTypeFilter: "All",
      typeFilterOptions: ["All", "Asset", "Liability", "Capital", "Income", "Expense"],
    };
  },
  computed: {
    differenceAmount() {
      return Math.abs((this.totalDebit || 0) - (this.totalCredit || 0));
    },
    isBookBalanced() {
      return this.isBalanced !== false && this.differenceAmount <= 0.01;
    },
    filteredRows() {
      if (!this.trialBalanceRows) return [];
      let list = this.trialBalanceRows;
      if (this.selectedTypeFilter && this.selectedTypeFilter !== "All") {
        list = list.filter((r) => r.type === this.selectedTypeFilter);
      }
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter((r) => {
          const nameMatch = r.name && r.name.toLowerCase().includes(q);
          const codeMatch = r.code && r.code.toLowerCase().includes(q);
          const typeMatch = r.type && r.type.toLowerCase().includes(q);
          return nameMatch || codeMatch || typeMatch;
        });
      }
      return list;
    },
  },
  mounted() {
    this.fetchTrialBalance();
  },
  methods: {
    resetFilters() {
      this.searchQuery = "";
      this.selectedTypeFilter = "All";
    },
    formatCurrency(val) {
      if (val === null || val === undefined || val === "") return "0.00";
      const num = Number(val);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getTypeColor(type) {
      switch (type) {
        case "Asset":
          return "#059669"; // emerald
        case "Liability":
          return "#d97706"; // amber
        case "Capital":
          return "#7c3aed"; // violet
        case "Income":
          return "#0d9488"; // teal
        case "Expense":
          return "#e11d48"; // rose
        default:
          return "#64748b";
      }
    },
    getTypeBadgeClass(type) {
      switch (type) {
        case "Asset":
          return "badge-asset";
        case "Liability":
          return "badge-liability";
        case "Capital":
          return "badge-capital";
        case "Income":
          return "badge-income";
        case "Expense":
          return "badge-expense";
        default:
          return "badge-default";
      }
    },
    getTypeIcon(type) {
      switch (type) {
        case "Asset":
          return "mdi-bank-outline";
        case "Liability":
          return "mdi-scale-balance";
        case "Capital":
          return "mdi-safe";
        case "Income":
          return "mdi-trending-up";
        case "Expense":
          return "mdi-receipt-text-outline";
        default:
          return "mdi-folder-outline";
      }
    },
    getTypeIconBoxClass(type) {
      switch (type) {
        case "Asset":
          return "icon-box-asset";
        case "Liability":
          return "icon-box-liability";
        case "Capital":
          return "icon-box-capital";
        case "Income":
          return "icon-box-income";
        case "Expense":
          return "icon-box-expense";
        default:
          return "icon-box-default";
      }
    },
    onDateChange(val) {
      if (this.$refs.menu) {
        this.$refs.menu.save(val);
      }
      this.fetchTrialBalance();
    },
    fetchTrialBalance() {
      if (!this.asOf) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/accounting/trialbalance",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          asOf: this.asOf,
        },
      })
        .then((res) => {
          this.appLoading = false;
          if (res.data.status) {
            this.trialBalanceRows = res.data.rows || [];
            this.totalDebit = res.data.totalDebit || 0;
            this.totalCredit = res.data.totalCredit || 0;
            this.isBalanced = res.data.isBalanced !== false;
          } else {
            this.msg = res.data.msg || "Failed to load Trial Balance";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    download() {
      if (!this.asOf) {
        this.msg = "Please choose a date";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/trialbalance",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          asOf: this.asOf,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `TrialBalance_${this.asOf}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@400;500;600;700&display=swap");

.trial-balance-page {
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #1e293b;
  padding-bottom: 32px;
}

/* Page Header */
.page-header-wrap {
  padding: 2px 0;
}

.breadcrumbs-row {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  letter-spacing: -0.01em;
}

.breadcrumb-sep {
  color: #94a3b8;
  margin: 0 6px;
  font-weight: 300;
}

.breadcrumb-link {
  color: #64748b !important;
  text-decoration: none;
  transition: color 0.15s ease;
}

.breadcrumb-link:hover {
  color: #9a6b27 !important;
}

.breadcrumb-item.active {
  color: #9a6b27;
  font-weight: 600;
}

.page-main-title {
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 25px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
  margin: 0;
  letter-spacing: -0.025em;
}

.page-subtitle {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13.5px;
  color: #64748b;
  font-weight: 400;
  margin-top: 3px;
  letter-spacing: -0.01em;
}

/* Header Right Actions */
.date-picker-wrap {
  width: 175px;
}

.date-input-field >>> .v-input__slot {
  min-height: 38px !important;
  height: 38px !important;
  font-size: 13px !important;
  padding: 0 10px !important;
  border-radius: 8px !important;
  background-color: #ffffff !important;
  border-color: #e2e8f0 !important;
  cursor: pointer !important;
}

.date-input-field >>> .v-text-field__prefix {
  font-size: 11px !important;
  font-weight: 700 !important;
  color: #64748b !important;
  text-transform: uppercase !important;
  letter-spacing: 0.04em !important;
  margin-top: 2px !important;
}

.date-input-field >>> input {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  color: #0f172a !important;
  cursor: pointer !important;
}

.date-input-field >>> .v-input__prepend-inner {
  margin-top: 7px !important;
  margin-right: 4px !important;
}

.date-input-field >>> .v-icon {
  font-size: 16px !important;
  color: #9a6b27 !important;
}

.btn-download-pdf {
  height: 38px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-download-pdf:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1px);
}

.btn-action-outline {
  height: 38px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  color: #334151 !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 14px !important;
  letter-spacing: -0.01em !important;
  transition: all 0.2s ease !important;
}

.btn-action-outline:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
  background-color: #fdfaf6 !important;
}

.btn-icon-only {
  min-width: 38px !important;
  width: 38px !important;
  padding: 0 !important;
}

/* Summary Metrics Cards Grid */
.summary-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

@media (max-width: 900px) {
  .summary-cards-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

.metric-card {
  background: #ffffff;
  border: 1px solid #e8e4dc;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-1.5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.metric-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.metric-icon-circle {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-debit {
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.icon-credit {
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
}

.icon-balanced {
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.icon-unbalanced {
  background-color: #fff1f2;
  border: 1px solid #fecdd3;
}

.metric-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.metric-caption {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.metric-card-debit {
  border-left: 4px solid #059669;
}

.metric-card-credit {
  border-left: 4px solid #2563eb;
}

.metric-card-balanced {
  border-left: 4px solid #059669;
  background: linear-gradient(180deg, #ffffff 0%, #fafffc 100%);
}

.metric-card-unbalanced {
  border-left: 4px solid #e11d48;
  background: linear-gradient(180deg, #ffffff 0%, #fffbfb 100%);
}

.text-debit {
  color: #059669;
}

.text-credit {
  color: #2563eb;
}

.text-balanced {
  color: #059669;
}

.text-unbalanced {
  color: #e11d48;
}

.text-unbalanced-sub {
  color: #e11d48;
  font-weight: 600;
}

/* Unbalanced Alert Banner */
.unbalanced-alert-banner {
  background-color: #fff5f5 !important;
  border-left-width: 4px !important;
}

.alert-title {
  font-size: 14px;
  color: #be123c;
}

.alert-subtitle {
  font-size: 13px;
  color: #475569;
}

/* Table Card */
.table-card {
  background-color: #ffffff;
  border: 1px solid #e8e4dc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) !important;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

.border-top {
  border-top: 1px solid #f1f5f9;
}

.table-card-top-bar {
  background-color: #ffffff;
}

.section-title {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.active-count-pill {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
}

.filter-type-select {
  width: 140px;
}

.filter-type-select >>> .v-input__slot {
  min-height: 36px !important;
  height: 36px !important;
  font-size: 12.5px !important;
  border-radius: 8px !important;
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
}

.filter-search-input {
  width: 250px;
}

.filter-search-input >>> .v-input__slot {
  min-height: 36px !important;
  height: 36px !important;
  font-size: 12.5px !important;
  border-radius: 8px !important;
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
}

.filter-search-input >>> input {
  font-size: 12.5px !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}

/* Custom Table */
.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.custom-table thead {
  background-color: #fafaf9;
}

.custom-table th {
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid #f1f5f9;
  user-select: none;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
}

.custom-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1e293b;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
}

.table-row {
  transition: background-color 0.12s ease;
}

.table-row:hover {
  background-color: #fafaf9;
}

/* Column Widths */
.col-sno {
  width: 48px;
  min-width: 44px;
}

.col-name {
  min-width: 200px;
}

.col-code {
  width: 120px;
}

.col-type {
  width: 130px;
}

.col-debit,
.col-credit {
  width: 160px;
}

.font-mono {
  font-variant-numeric: tabular-nums;
}

.text-muted {
  color: #94a3b8 !important;
  font-size: 12px;
}

.text-muted-dash {
  color: #cbd5e1;
  font-size: 14px;
}

/* Account Name Cell */
.account-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-box-asset { background-color: #ecfdf5; border: 1px solid #d1fae5; }
.icon-box-liability { background-color: #fffbeb; border: 1px solid #fef3c7; }
.icon-box-capital { background-color: #f5f3ff; border: 1px solid #ede9fe; }
.icon-box-income { background-color: #f0fdfa; border: 1px solid #ccfbf1; }
.icon-box-expense { background-color: #fff1f2; border: 1px solid #ffe4e6; }
.icon-box-default { background-color: #f8fafc; border: 1px solid #e2e8f0; }

.account-name-text {
  font-size: 13.5px;
  font-weight: 600;
  color: #0f172a;
}

/* Code Badge */
.code-badge {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.02em;
}

/* Type Badge */
.type-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 9999px;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

.type-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 6px;
  display: inline-block;
  flex-shrink: 0;
}

.badge-asset { background-color: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
.badge-liability { background-color: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
.badge-capital { background-color: #f5f3ff; color: #6d28d9; border: 1px solid #ddd6fe; }
.badge-income { background-color: #f0fdfa; color: #0f766e; border: 1px solid #99f6e4; }
.badge-expense { background-color: #fff1f2; color: #be123c; border: 1px solid #fecdd3; }
.badge-default { background-color: #f8fafc; color: #475569; border: 1px solid #e2e8f0; }

.bg-asset { background-color: #059669; }
.bg-liability { background-color: #d97706; }
.bg-capital { background-color: #7c3aed; }
.bg-income { background-color: #0d9488; }
.bg-expense { background-color: #e11d48; }

/* Debit & Credit Values in Table */
.debit-val {
  font-weight: 700;
  color: #059669;
  font-size: 13.5px;
}

.credit-val {
  font-weight: 700;
  color: #2563eb;
  font-size: 13.5px;
}

/* Totals Row */
.table-totals-foot {
  background-color: #fafaf9;
  border-top: 2px solid #e2e8f0;
}

.table-totals-foot td {
  padding: 14px 16px;
  border-bottom: none !important;
}

.totals-label-cell {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.03em;
}

.totals-text {
  font-weight: 700;
}

.totals-debit-val {
  font-size: 15px;
  font-weight: 800;
  color: #059669;
  letter-spacing: -0.01em;
}

.totals-credit-val {
  font-size: 15px;
  font-weight: 800;
  color: #2563eb;
  letter-spacing: -0.01em;
}

/* Empty State */
.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  font-size: 13px;
  color: #64748b;
}

/* Table Footer */
.table-card-footer {
  background-color: #fafaf9;
}

.footer-summary-text {
  font-size: 12px;
  color: #64748b;
}

.legend-item {
  font-size: 11px;
  color: #64748b;
  display: inline-flex;
  align-items: center;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

@media (max-width: 600px) {
  .date-picker-wrap {
    width: 100% !important;
    margin-right: 0 !important;
    margin-bottom: 8px;
  }
  .filter-search-input,
  .filter-type-select {
    width: 100% !important;
  }
}
</style>
